import { useState, useEffect, useCallback } from 'react';

import ReactMapGL from 'react-map-gl/maplibre';
import WebMercatorViewport from '@math.gl/web-mercator';
import { H3HexagonLayer } from '@deck.gl/geo-layers';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { mapViewport, dwellTimeResolutionSelector, useViewPortUpdate, dwellTimes } from './States';
import DeckGLOverlay from '@/components/DeckGLOverlay';

const mapStyle = `${import.meta.env.REACT_APP_MAP_TILER_URI}?key=${import.meta.env.REACT_APP_MAP_TILER_KEY}`;

export default function DwellTimeStats() {
  const INITIAL_VIEW_STATE = {
    longitude: -122.41669,
    latitude: 37.7853,
    zoom: 8,
    pitch: 0,
    minZoom: 4,
    bearing: 0,
    // width: '100%',
    // height: '100%'
  };

  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
  const MAPBOX_ACCESS_TOKEN = `${import.meta.env.REACT_APP_MAPBOX_KEY}`;
  const [layers, setLayers] = useState([]);
  const setMapViewPort = useSetRecoilState(mapViewport);
  const dwellTimeResolution = useRecoilValue(dwellTimeResolutionSelector);
  const loadDwellTimes = useViewPortUpdate();
  const data = useRecoilValue(dwellTimes);

  // const solution = useRecoilValue(selectedRoutingSolution)

  const handleResize = useCallback((event) => {
    const container = event.target.getContainer();
    const width = container.clientWidth;
    const height = container.clientHeight;
    setViewState((prev) => ({ ...prev, width, height }));
  }, []);

  useEffect(() => {
    if (!viewState.width || !viewState.height) {
      return;
    }
    try {
      const viewport = new WebMercatorViewport(viewState);
      const bounds = viewport.getBounds();
      setMapViewPort({ zoom: viewState.zoom, bounds });
    } catch (err) {
      // console.error('Error creating WebMercatorViewport or getting bounds:', err);
    }
  }, [viewState]);

  useEffect(() => {
    if (!data?.data || !data.resolution) {
      return;
    }
    const layer = new H3HexagonLayer({
      id: `h3-hexagon-layer-${data.resolution}`,
      data: data.data,
      pickable: true,
      wireframe: false,
      filled: true,
      extruded: true,
      elevationScale: 2,
      getHexagon: (d) => d.hex,
      getFillColor: (d) =>
        d.avg > 135
          ? [255, 255 - (255 * (Math.min(d.avg, 420) - 135)) / 285, 10, 150]
          : [(255 * (Math.max(d.avg, 40) - 40)) / 135, 255, 10, 150],
      getElevation: (d) => d.count,
    });
    setLayers([layer]);
  }, [data]);

  useEffect(() => {
    loadDwellTimes();
  }, [dwellTimeResolution]);

  const onViewStateChange = ({ viewState, interactionState }) => {
    if (!interactionState.inTransition) setViewState(viewState);
  };

  const handleChangeViewState = useCallback(({ viewState }) => {
    setViewState(viewState);
  }, []);

  return (
    <ReactMapGL
      {...viewState}
      onMove={handleChangeViewState}
      onResize={handleResize}
      mapStyle={mapStyle}
      style={{ width: '100%', height: 'calc(100vh - 64px)' }}
      attributionControl={false}
    >
      <DeckGLOverlay
        layers={layers}
        getTooltip={({ object }) =>
          object &&
          `count: ${object.count ?? 'N/A'} avg: ${typeof object.avg === 'number' ? object.avg.toFixed(0) : 'N/A'} (s)`
        }
      />
    </ReactMapGL>
  );
}
